import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Spinner,
  Form,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import apiClient from "../../../api/apiClient";
import AIChatbotMessagesFilter, { MessagesFilterValues } from "./AIChatbotMessagesFilter";
import { Icon } from "../../../components/common/Icon";
import {
  FaEye,
  FaStar,
  FaRegStar,
  FaStickyNote,
  FaTags,
  FaRobot,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import AIChatbotConversationModal from "./AIChatbotConversationModal";
import aiChatbotsService, {
  ConversationTag,
  AnnotationInline,
} from "../../../features/ai_chatbots/aiChatbotsService";

type SessionRow = {
  conversation_id: number;
  session_id: string;
  message_count: number;
  first_message_at: string | null;
  last_message_at: string | null;
  annotation?: AnnotationInline | null;
};

interface AIChatbotMessagesModalProps {
  show: boolean;
  onHide: () => void;
  chatbotId: number;
  chatbotName: string;
}

const LIMIT_OPTIONS = [10, 25, 50, 100];

const AIChatbotMessagesModal: React.FC<AIChatbotMessagesModalProps> = ({
  show,
  onHide,
  chatbotId,
  chatbotName,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(LIMIT_OPTIONS[0]);

  const [textSearch, setTextSearch] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [role, setRole] = useState<"user" | "assistant" | "">("");

  // Annotation filters
  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(undefined);
  const [hasNote, setHasNote] = useState<boolean | undefined>(undefined);
  const [filterTagIds, setFilterTagIds] = useState<number[]>([]);
  const [availableTags, setAvailableTags] = useState<ConversationTag[]>([]);
  const [tagFilterSearch, setTagFilterSearch] = useState("");
  const [tagFilterExpanded, setTagFilterExpanded] = useState(false);

  const [showConversation, setShowConversation] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string>("");
  const [favoriteLoading, setFavoriteLoading] = useState<number | null>(null);
  const [chatbotCompanyId, setChatbotCompanyId] = useState<number | null>(null);

  const totalPages = useMemo(
    () => (totalCount > 0 ? Math.ceil(totalCount / limit) : 0),
    [totalCount, limit]
  );

  const annotationFilterVersion = useMemo(
    () => JSON.stringify({ isFavorite, hasNote, filterTagIds }),
    [isFavorite, hasNote, filterTagIds]
  );

  useEffect(() => {
    if (show) {
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, chatbotId, limit, annotationFilterVersion]);

  useEffect(() => {
    if (!show || !chatbotId) {
      if (!show) setChatbotCompanyId(null);
      return;
    }
    let cancelled = false;
    aiChatbotsService
      .getChatbot(chatbotId)
      .then((c) => {
        if (!cancelled) setChatbotCompanyId(c.company_id);
      })
      .catch(() => {
        if (!cancelled) setChatbotCompanyId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [show, chatbotId]);

  useEffect(() => {
    setFilterTagIds([]);
  }, [chatbotId]);

  const loadTags = useCallback(async (companyId: number) => {
    try {
      const response = await aiChatbotsService.getConversationTags({
        limit: 200,
        company_id: companyId,
      });
      const tagData = (response as any).data || response || [];
      setAvailableTags(Array.isArray(tagData) ? tagData : []);
    } catch {
      setAvailableTags([]);
    }
  }, []);

  useEffect(() => {
    if (show && chatbotCompanyId != null) {
      loadTags(chatbotCompanyId);
    }
  }, [show, chatbotCompanyId, loadTags]);

  type FilterState = {
    sessionId: string;
    textSearch: string;
    startDate: string;
    endDate: string;
    role: "user" | "assistant" | "";
  };

  const fetchPage = async (
    nextPage: number,
    overrides?: Partial<FilterState>
  ) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const skip = (nextPage - 1) * limit;
      const effective: FilterState = {
        sessionId: overrides?.sessionId ?? sessionId,
        textSearch: overrides?.textSearch ?? textSearch,
        startDate: overrides?.startDate ?? startDate,
        endDate: overrides?.endDate ?? endDate,
        role: overrides?.role ?? role,
      };
      const params: any = { skip, limit };
      if (effective.sessionId.trim()) params.session_id = effective.sessionId.trim();
      if (effective.textSearch.trim()) params.text_search = effective.textSearch.trim();
      if (effective.startDate) params.start_date = effective.startDate;
      if (effective.endDate) params.end_date = effective.endDate;
      if (effective.role) params.role = effective.role;
      if (isFavorite !== undefined) params.is_favorite = isFavorite;
      if (hasNote !== undefined) params.has_note = hasNote;
      if (filterTagIds.length > 0) params.tag_ids = filterTagIds.join(",");

      const resp = await apiClient.get(
        `/ai-chatbots/${chatbotId}/messages/list`,
        { params }
      ) as any;
      const rawData = resp?.data;
      const pagination = resp?.pagination ?? rawData?.pagination;
      const rawItems = Array.isArray(rawData)
        ? rawData
        : Array.isArray(rawData?.data)
          ? rawData.data
          : [];
      const items: SessionRow[] = rawItems.map((raw: any) => ({
        conversation_id: Number(raw.conversation_id ?? raw.conversationId ?? 0),
        session_id: String(raw.session_id ?? raw.sessionId ?? ""),
        message_count: Number(raw.message_count ?? raw.messageCount ?? 0),
        first_message_at: raw.first_message_at ?? raw.firstMessageAt ?? null,
        last_message_at: raw.last_message_at ?? raw.lastMessageAt ?? null,
        annotation: raw.annotation ?? null,
      }));
      setRows(items);
      setTotalCount(pagination?.total_count != null ? Number(pagination.total_count) : items.length);
      setPage(nextPage);
    } catch (e: any) {
      console.error("messages_list_fetch_error", e);
      setError(t("failed_to_load_messages") || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setTextSearch("");
    setSessionId("");
    setStartDate("");
    setEndDate("");
    setRole("");
  };

  const handleToggleFavorite = async (conversationId: number) => {
    setFavoriteLoading(conversationId);
    try {
      const result = await aiChatbotsService.toggleConversationFavorite(conversationId);
      setRows(prev =>
        prev.map(r =>
          r.conversation_id === conversationId
            ? {
                ...r,
                annotation: {
                  id: result.id,
                  is_favorite: result.is_favorite,
                  notes: result.notes,
                  tags: result.tags?.map(t => ({ id: t.id, name: t.name, color: t.color })) || [],
                },
              }
            : r
        )
      );
    } catch (err) {
      console.error("Error toggling favorite:", err);
    } finally {
      setFavoriteLoading(null);
    }
  };

  const openConversation = (conversationId: number, sessId: string) => {
    setSelectedConversationId(conversationId);
    setSelectedSessionId(sessId);
    setShowConversation(true);
  };

  const handleConversationClose = () => {
    setShowConversation(false);
    if (chatbotCompanyId != null) loadTags(chatbotCompanyId);
    fetchPage(page);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return t("just_now") || "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  };

  const activeAnnotationFilters = (isFavorite !== undefined ? 1 : 0) + (hasNote !== undefined ? 1 : 0) + (filterTagIds.length > 0 ? 1 : 0);

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        className="premium-messages-modal"
        backdrop="static"
      >
        <div className="pm-wrapper">
          {/* Header */}
          <div className="pm-header">
            <div className="pm-header-left">
              <div className="pm-avatar">
                <Icon icon={FaRobot} />
              </div>
              <div className="pm-header-info">
                <h5 className="pm-title">{chatbotName}</h5>
                <span className="pm-subtitle">{t("sessions") || "Sessions"} ({totalCount})</span>
              </div>
            </div>
            <button className="pm-close-btn" onClick={onHide}>&times;</button>
          </div>

          {/* Filter Section */}
          <div className="pm-filter-section">
            <AIChatbotMessagesFilter
              className="filter-card"
              loading={loading}
              initialValues={{
                session_id: sessionId || undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                text_search: textSearch || undefined,
                role: (role as any) || undefined,
              }}
              onReset={resetFilters}
              onFilterChange={(vals: MessagesFilterValues) => {
                const nextFilters: FilterState = {
                  sessionId: vals.session_id || "",
                  startDate: vals.start_date || "",
                  endDate: vals.end_date || "",
                  textSearch: vals.text_search || "",
                  role: (vals.role as any) || "",
                };
                setSessionId(nextFilters.sessionId);
                setStartDate(nextFilters.startDate);
                setEndDate(nextFilters.endDate);
                setTextSearch(nextFilters.textSearch);
                setRole(nextFilters.role);
                fetchPage(1, nextFilters);
              }}
            />

            {/* Annotation Filter Chips */}
            <div className="pm-annotation-filters">
              <button
                className={`pm-chip ${isFavorite === true ? "active" : ""}`}
                onClick={() => setIsFavorite(prev => prev === true ? undefined : true)}
              >
                <Icon icon={FaStar} />
                <span>{t("favorites") || "Favorites"}</span>
              </button>
              <button
                className={`pm-chip ${hasNote === true ? "active" : ""}`}
                onClick={() => setHasNote(prev => prev === true ? undefined : true)}
              >
                <Icon icon={FaStickyNote} />
                <span>{t("has_note") || "Has Note"}</span>
              </button>
              {availableTags.length > 0 && (
                <div className="pm-tag-filter-group">
                  <Icon icon={FaTags} className="pm-tag-filter-icon" />
                  <div className="pm-tag-filter-search-wrap">
                    <Icon icon={FaSearch} className="pm-tag-search-icon" />
                    <input
                      type="text"
                      className="pm-tag-filter-search"
                      placeholder={t("search_tags")}
                      value={tagFilterSearch}
                      onChange={(e) => setTagFilterSearch(e.target.value)}
                      aria-label={t("search_tags")}
                    />
                  </div>
                  {(() => {
                    const q = tagFilterSearch.trim().toLowerCase();
                    const filtered = q
                      ? availableTags.filter((t) => t.name.toLowerCase().includes(q))
                      : availableTags;
                    const visibleCount = 8;
                    const visible = tagFilterExpanded ? filtered : filtered.slice(0, visibleCount);
                    const hasMore = filtered.length > visibleCount;
                    if (filtered.length === 0) {
                      return (
                        <span className="pm-tag-no-match">{t("no_matching_tags")}</span>
                      );
                    }
                    return (
                      <>
                        <div className="pm-tag-chips-wrap">
                          {visible.map((tag) => (
                            <button
                              key={tag.id}
                              className={`pm-tag-chip ${filterTagIds.includes(tag.id) ? "active" : ""}`}
                              style={{
                                "--tag-color": tag.color,
                                "--tag-color-bg": `${tag.color}15`,
                              } as React.CSSProperties}
                              onClick={() =>
                                setFilterTagIds((prev) =>
                                  prev.includes(tag.id)
                                    ? prev.filter((id) => id !== tag.id)
                                    : [...prev, tag.id]
                                )
                              }
                            >
                              {tag.name}
                            </button>
                          ))}
                        </div>
                        {hasMore && (
                          <button
                            type="button"
                            className="pm-tag-show-more"
                            onClick={() => setTagFilterExpanded((v) => !v)}
                          >
                            {tagFilterExpanded ? (
                              <>
                                <Icon icon={FaChevronUp} />
                                {t("show_less")}
                              </>
                            ) : (
                              <>
                                <Icon icon={FaChevronDown} />
                                {t("show_more")} ({filtered.length - visibleCount})
                              </>
                            )}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {activeAnnotationFilters > 0 && (
                <button
                  className="pm-chip pm-chip-clear"
                  onClick={() => {
                    setIsFavorite(undefined);
                    setHasNote(undefined);
                    setFilterTagIds([]);
                  }}
                >
                  {t("clear") || "Clear"} ({activeAnnotationFilters})
                </button>
              )}
            </div>
          </div>

          {/* Sessions Table */}
          <div className="pm-table-section">
            <div className="pm-table-responsive">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th className="pm-col-fav"></th>
                    <th className="pm-col-activity">{t("last_activity") || "Last Activity"}</th>
                    <th className="pm-col-session">{t("session_id") || "Session"}</th>
                    <th className="pm-col-msgs">{t("messages") || "Msgs"}</th>
                    <th className="pm-col-tags">{t("tags") || "Tags"}</th>
                    <th className="pm-col-note"></th>
                    <th className="pm-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="pm-state-cell">
                        <div className="pm-loading">
                          <div className="pm-loading-dots">
                            <span></span><span></span><span></span>
                          </div>
                          <p>{t("loading_messages") || "Loading sessions..."}</p>
                        </div>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="pm-state-cell">
                        <div className="pm-empty">
                          <div className="pm-empty-icon">&#128172;</div>
                          <p>{t("no_messages_found") || "No sessions found"}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const ann = r.annotation;
                      const isFav = ann?.is_favorite ?? false;
                      const tags = ann?.tags ?? [];
                      const hasNoteValue = !!(ann?.notes && ann.notes.length > 0);
                      const noteTitle = hasNoteValue && ann?.notes
                        ? ann.notes.map(n => `${n.user_name}: ${n.note_text.slice(0, 50)}${n.note_text.length > 50 ? "…" : ""}`).join("\n")
                        : "";

                      return (
                        <tr key={`${r.conversation_id}-${idx}`} className="pm-row">
                          <td className="pm-col-fav">
                            <button
                              className={`pm-fav-btn ${isFav ? "active" : ""}`}
                              onClick={() => handleToggleFavorite(r.conversation_id)}
                              disabled={favoriteLoading === r.conversation_id}
                              title={isFav ? t("remove_favorite") || "Remove favorite" : t("add_favorite") || "Add favorite"}
                            >
                              {favoriteLoading === r.conversation_id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Icon icon={isFav ? FaStar : FaRegStar} />
                              )}
                            </button>
                          </td>
                          <td className="pm-col-activity">
                            <span className="pm-time-text">{formatDate(r.last_message_at)}</span>
                          </td>
                          <td className="pm-col-session">
                            <code className="pm-session-code">{r.session_id}</code>
                          </td>
                          <td className="pm-col-msgs">
                            <span className="pm-msg-badge">{r.message_count}</span>
                          </td>
                          <td className="pm-col-tags">
                            <div className="pm-tags-cell">
                              {tags.slice(0, 3).map(tag => (
                                <span
                                  key={tag.id}
                                  className="pm-tag-badge"
                                  style={{ backgroundColor: `${tag.color}20`, color: tag.color, borderColor: `${tag.color}40` }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {tags.length > 3 && (
                                <span className="pm-tag-more">+{tags.length - 3}</span>
                              )}
                            </div>
                          </td>
                          <td className="pm-col-note">
                            {hasNoteValue && (
                              <span className="pm-note-indicator" title={noteTitle}>
                                <Icon icon={FaStickyNote} />
                              </span>
                            )}
                          </td>
                          <td className="pm-col-actions">
                            <button
                              className="pm-view-btn"
                              title={t("view_conversation") || "View"}
                              onClick={() => openConversation(r.conversation_id, r.session_id)}
                            >
                              <Icon icon={FaEye} />
                              <span>{t("view") || "View"}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer / Pagination */}
          <div className="pm-footer">
            <div className="pm-footer-left">
              <span className="pm-footer-count">
                {t("showing") || "Showing"} <strong>{rows.length}</strong> {t("of") || "of"} <strong>{totalCount}</strong>
              </span>
            </div>
            <div className="pm-footer-right">
              <div className="pm-per-page">
                <span>{t("per_page") || "Per page"}:</span>
                <Form.Select
                  size="sm"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                  className="pm-limit-select"
                >
                  {LIMIT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="pm-pagination">
                <button className="pm-page-btn" onClick={() => fetchPage(1)} disabled={page <= 1 || loading}>
                  <Icon icon={FaAngleDoubleLeft} />
                </button>
                <button className="pm-page-btn" onClick={() => fetchPage(Math.max(1, page - 1))} disabled={page <= 1 || loading}>
                  <Icon icon={FaChevronLeft} />
                </button>
                <span className="pm-page-info">{page} / {totalPages || 1}</span>
                <button className="pm-page-btn" onClick={() => fetchPage(Math.min(totalPages || 1, page + 1))} disabled={page >= (totalPages || 1) || loading}>
                  <Icon icon={FaChevronRight} />
                </button>
                <button className="pm-page-btn" onClick={() => fetchPage(totalPages || 1)} disabled={page >= (totalPages || 1) || loading}>
                  <Icon icon={FaAngleDoubleRight} />
                </button>
              </div>
            </div>
          </div>

          {error && <div className="pm-error">{error}</div>}
        </div>
      </Modal>

      {showConversation && selectedConversationId && (
        <AIChatbotConversationModal
          show={showConversation}
          onHide={handleConversationClose}
          conversationId={selectedConversationId}
          sessionId={selectedSessionId}
          chatbotId={chatbotId}
          chatbotName={chatbotName}
        />
      )}

      <style>{`
        .premium-messages-modal {
          --pm-primary: #667eea;
          --pm-primary-dark: #5a6fd8;
          --pm-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --pm-gold: #f5a623;
          --pm-gold-bg: rgba(245, 166, 35, 0.08);
          --pm-note: #8b5cf6;
          --pm-note-bg: rgba(139, 92, 246, 0.08);
          --pm-surface: #ffffff;
          --pm-bg: #f8f9fc;
          --pm-border: #e8eaf0;
          --pm-border-light: #f0f1f5;
          --pm-text: #1a1d26;
          --pm-text-secondary: #6b7280;
          --pm-text-muted: #9ca3af;
          --pm-radius: 16px;
          --pm-radius-sm: 8px;
          --pm-radius-xs: 6px;
          --pm-radius-full: 9999px;
          --pm-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
          --pm-shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06);
          --pm-transition: all 0.2s ease;
        }

        .premium-messages-modal .modal-dialog {
          max-width: 1100px;
          margin: 1rem auto;
        }

        .premium-messages-modal .modal-content {
          border: none;
          border-radius: var(--pm-radius);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          height: 90vh;
          max-height: 860px;
          background: var(--pm-bg);
        }

        .pm-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Header */
        .pm-header {
          background: var(--pm-gradient);
          padding: 1.25rem 1.75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          flex-shrink: 0;
        }

        .pm-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: rgba(255,255,255,0.15);
        }

        .pm-header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .pm-avatar {
          width: 44px;
          height: 44px;
          background: rgba(255,255,255,0.18);
          border-radius: var(--pm-radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          border: 1.5px solid rgba(255,255,255,0.25);
          backdrop-filter: blur(8px);
        }

        .pm-header-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .pm-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 700;
          color: white;
          letter-spacing: -0.01em;
        }

        .pm-subtitle {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.75);
          font-weight: 500;
        }

        .pm-close-btn {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: var(--pm-radius-full);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          cursor: pointer;
          transition: var(--pm-transition);
          line-height: 1;
        }

        .pm-close-btn:hover {
          background: rgba(255,255,255,0.25);
        }

        /* Filter Section */
        .pm-filter-section {
          padding: 1rem 1.5rem 0.75rem;
          background: var(--pm-surface);
          border-bottom: 1px solid var(--pm-border);
          flex-shrink: 0;
        }

        .pm-filter-section .filter-card {
          margin-bottom: 0.5rem;
        }

        /* Annotation Filter Chips */
        .pm-annotation-filters {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          padding-top: 0.5rem;
        }

        .pm-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.375rem 0.875rem;
          border: 1.5px solid var(--pm-border);
          border-radius: var(--pm-radius-full);
          background: var(--pm-surface);
          color: var(--pm-text-secondary);
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: var(--pm-transition);
          white-space: nowrap;
        }

        .pm-chip:hover {
          border-color: var(--pm-primary);
          color: var(--pm-primary);
          background: rgba(102, 126, 234, 0.04);
        }

        .pm-chip.active {
          background: var(--pm-primary);
          color: white;
          border-color: var(--pm-primary);
        }

        .pm-chip.active:hover {
          background: var(--pm-primary-dark);
        }

        .pm-chip svg {
          font-size: 0.75rem;
        }

        .pm-chip-clear {
          border-color: #ef4444;
          color: #ef4444;
          background: rgba(239, 68, 68, 0.04);
        }

        .pm-chip-clear:hover {
          background: #ef4444 !important;
          color: white !important;
          border-color: #ef4444 !important;
        }

        .pm-tag-filter-group {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
          padding-left: 0.5rem;
          border-left: 1px solid var(--pm-border);
          margin-left: 0.25rem;
          max-width: 100%;
        }

        .pm-tag-filter-icon {
          color: var(--pm-text-muted);
          font-size: 0.75rem;
          margin-right: 0.125rem;
          flex-shrink: 0;
        }

        .pm-tag-filter-search-wrap {
          position: relative;
          flex-shrink: 0;
        }

        .pm-tag-filter-search-wrap .pm-tag-search-icon {
          position: absolute;
          left: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--pm-text-muted);
          font-size: 0.7rem;
          pointer-events: none;
        }

        .pm-tag-filter-search {
          width: 120px;
          padding: 0.35rem 0.5rem 0.35rem 1.5rem;
          border: 1px solid var(--pm-border);
          border-radius: var(--pm-radius-xs);
          font-size: 0.75rem;
          background: var(--pm-surface);
          color: var(--pm-text);
          outline: none;
          transition: var(--pm-transition);
        }

        .pm-tag-filter-search::placeholder {
          color: var(--pm-text-muted);
        }

        .pm-tag-filter-search:focus {
          border-color: var(--pm-primary);
          width: 140px;
        }

        .pm-tag-chips-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 0.375rem;
          align-items: center;
        }

        .pm-tag-show-more {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          border: 1px dashed var(--pm-border);
          border-radius: var(--pm-radius-xs);
          background: transparent;
          color: var(--pm-text-muted);
          font-size: 0.7rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--pm-transition);
          flex-shrink: 0;
        }

        .pm-tag-show-more:hover {
          border-color: var(--pm-primary);
          color: var(--pm-primary);
          background: rgba(102, 126, 234, 0.04);
        }

        .pm-tag-show-more svg {
          font-size: 0.65rem;
        }

        .pm-tag-no-match {
          font-size: 0.75rem;
          color: var(--pm-text-muted);
          font-style: italic;
        }

        .pm-tag-chip {
          display: inline-flex;
          align-items: center;
          padding: 0.3rem 0.7rem;
          border: 1.5px solid var(--tag-color, var(--pm-border));
          border-radius: var(--pm-radius-full);
          background: var(--tag-color-bg, transparent);
          color: var(--tag-color, var(--pm-text-secondary));
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--pm-transition);
        }

        .pm-tag-chip:hover {
          opacity: 0.85;
          transform: translateY(-1px);
        }

        .pm-tag-chip.active {
          background: var(--tag-color, var(--pm-primary));
          color: white;
          border-color: var(--tag-color, var(--pm-primary));
        }

        /* Table Section */
        .pm-table-section {
          flex: 1;
          overflow-y: auto;
          padding: 0;
        }

        .pm-table-responsive {
          min-height: 100%;
        }

        .pm-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .pm-table thead {
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .pm-table thead tr {
          background: var(--pm-bg);
        }

        .pm-table th {
          padding: 0.75rem 1rem;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--pm-text-muted);
          border-bottom: 1px solid var(--pm-border);
          white-space: nowrap;
          user-select: none;
        }

        .pm-table tbody tr {
          transition: var(--pm-transition);
        }

        .pm-row {
          background: var(--pm-surface);
          border-bottom: 1px solid var(--pm-border-light);
        }

        .pm-row:hover {
          background: rgba(102, 126, 234, 0.025);
        }

        .pm-row:last-child {
          border-bottom: none;
        }

        .pm-table td {
          padding: 0.75rem 1rem;
          vertical-align: middle;
          font-size: 0.875rem;
        }

        /* Column widths */
        .pm-col-fav { width: 44px; text-align: center; padding-left: 1.25rem !important; }
        .pm-col-activity { width: 120px; }
        .pm-col-session { }
        .pm-col-msgs { width: 72px; text-align: center; }
        .pm-col-tags { width: 200px; }
        .pm-col-note { width: 40px; text-align: center; }
        .pm-col-actions { width: 100px; text-align: right; padding-right: 1.25rem !important; }

        /* Favorite button */
        .pm-fav-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: var(--pm-radius-full);
          color: var(--pm-text-muted);
          font-size: 0.95rem;
          transition: var(--pm-transition);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
        }

        .pm-fav-btn:hover {
          color: var(--pm-gold);
          background: var(--pm-gold-bg);
        }

        .pm-fav-btn.active {
          color: var(--pm-gold);
        }

        .pm-fav-btn.active:hover {
          background: var(--pm-gold-bg);
        }

        .pm-fav-btn:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        /* Time text */
        .pm-time-text {
          color: var(--pm-text-secondary);
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* Session code */
        .pm-session-code {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
          font-size: 0.78rem;
          color: var(--pm-primary-dark);
          background: rgba(102, 126, 234, 0.06);
          padding: 0.25rem 0.5rem;
          border-radius: var(--pm-radius-xs);
          border: 1px solid rgba(102, 126, 234, 0.12);
          word-break: break-all;
        }

        /* Message count badge */
        .pm-msg-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 24px;
          padding: 0 0.5rem;
          background: var(--pm-bg);
          border: 1px solid var(--pm-border);
          border-radius: var(--pm-radius-full);
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--pm-text-secondary);
        }

        /* Tags cell */
        .pm-tags-cell {
          display: flex;
          flex-wrap: wrap;
          gap: 0.3rem;
          align-items: center;
        }

        .pm-tag-badge {
          display: inline-block;
          padding: 0.2rem 0.55rem;
          border-radius: var(--pm-radius-full);
          font-size: 0.7rem;
          font-weight: 600;
          border: 1px solid;
          white-space: nowrap;
          line-height: 1.4;
        }

        .pm-tag-more {
          font-size: 0.7rem;
          color: var(--pm-text-muted);
          font-weight: 500;
        }

        /* Note indicator */
        .pm-note-indicator {
          color: var(--pm-note);
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: var(--pm-radius-full);
          background: var(--pm-note-bg);
          margin: 0 auto;
        }

        /* View button */
        .pm-view-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.4rem 0.875rem;
          background: var(--pm-surface);
          border: 1.5px solid var(--pm-border);
          border-radius: var(--pm-radius-sm);
          color: var(--pm-text-secondary);
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--pm-transition);
        }

        .pm-view-btn:hover {
          border-color: var(--pm-primary);
          color: var(--pm-primary);
          background: rgba(102, 126, 234, 0.04);
          transform: translateY(-1px);
          box-shadow: var(--pm-shadow-sm);
        }

        .pm-view-btn svg {
          font-size: 0.72rem;
        }

        /* States */
        .pm-state-cell {
          padding: 3rem !important;
          text-align: center;
        }

        .pm-loading, .pm-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .pm-loading p, .pm-empty p {
          color: var(--pm-text-muted);
          font-size: 0.9rem;
          margin: 0;
        }

        .pm-loading-dots {
          display: flex;
          gap: 0.4rem;
        }

        .pm-loading-dots span {
          width: 8px;
          height: 8px;
          background: var(--pm-primary);
          border-radius: var(--pm-radius-full);
          animation: pmBounce 1.4s ease-in-out infinite both;
        }

        .pm-loading-dots span:nth-child(1) { animation-delay: -0.32s; }
        .pm-loading-dots span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes pmBounce {
          0%, 80%, 100% { transform: scale(0.4); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }

        .pm-empty-icon {
          font-size: 2.5rem;
          opacity: 0.6;
        }

        /* Footer */
        .pm-footer {
          background: var(--pm-surface);
          border-top: 1px solid var(--pm-border);
          padding: 0.75rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          gap: 1rem;
        }

        .pm-footer-left {
          display: flex;
          align-items: center;
        }

        .pm-footer-count {
          font-size: 0.8rem;
          color: var(--pm-text-muted);
        }

        .pm-footer-count strong {
          color: var(--pm-text);
          font-weight: 700;
        }

        .pm-footer-right {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .pm-per-page {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--pm-text-muted);
        }

        .pm-limit-select {
          width: auto;
          border: 1.5px solid var(--pm-border) !important;
          border-radius: var(--pm-radius-xs) !important;
          font-size: 0.8rem !important;
          padding: 0.25rem 1.75rem 0.25rem 0.5rem !important;
          color: var(--pm-text) !important;
          background-color: var(--pm-surface) !important;
        }

        .pm-pagination {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .pm-page-btn {
          background: var(--pm-surface);
          border: 1.5px solid var(--pm-border);
          border-radius: var(--pm-radius-xs);
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--pm-text-secondary);
          cursor: pointer;
          transition: var(--pm-transition);
          font-size: 0.75rem;
        }

        .pm-page-btn:hover:not(:disabled) {
          border-color: var(--pm-primary);
          color: var(--pm-primary);
          background: rgba(102, 126, 234, 0.04);
        }

        .pm-page-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .pm-page-info {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--pm-text);
          padding: 0 0.5rem;
          min-width: 44px;
          text-align: center;
        }

        /* Error */
        .pm-error {
          padding: 0.75rem 1.5rem;
          background: rgba(239, 68, 68, 0.06);
          color: #ef4444;
          font-size: 0.8rem;
          text-align: center;
          border-top: 1px solid rgba(239, 68, 68, 0.1);
        }

        /* Scrollbar */
        .pm-table-section::-webkit-scrollbar { width: 6px; }
        .pm-table-section::-webkit-scrollbar-track { background: transparent; }
        .pm-table-section::-webkit-scrollbar-thumb {
          background: var(--pm-border);
          border-radius: 3px;
        }
        .pm-table-section::-webkit-scrollbar-thumb:hover {
          background: var(--pm-text-muted);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .premium-messages-modal .modal-dialog {
            margin: 0.5rem;
            max-width: none;
          }

          .premium-messages-modal .modal-content {
            height: 95vh;
            border-radius: 12px;
          }

          .pm-header { padding: 1rem 1.25rem; }
          .pm-filter-section { padding: 0.75rem 1rem 0.5rem; }
          .pm-footer { padding: 0.5rem 1rem; flex-wrap: wrap; }
          .pm-col-tags, .pm-col-note { display: none; }
          .pm-table th, .pm-table td { padding: 0.625rem 0.75rem; }

          .pm-annotation-filters { gap: 0.375rem; }
          .pm-tag-filter-group { border-left: none; padding-left: 0; margin-left: 0; }
        }

        @media (max-width: 480px) {
          .pm-col-activity { display: none; }
          .pm-footer-right { flex-direction: column; gap: 0.5rem; align-items: flex-end; }
        }
      `}</style>
    </>
  );
};

export default AIChatbotMessagesModal;
